import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './Icon';

/* ==========================================================================
   قائمةٌ تُفتح ويُضاف إليها
   ========================================================================== */

/**
 * قائمةٌ منسدلةٌ بخياراتٍ موجودة، ويُضاف إليها من داخلها.
 *
 * الحقلُ الحرُّ مع «datalist» كان يترك القائمةَ مخبوءةً خلف سهمٍ لا يُرى،
 * فيظنّ القارئُ ألّا خيارات ويكتب من رأسه — فتتكاثر «تصوير» و«التصوير»
 * و«تصوير فيديو» ولا يجمعها تصنيف. وقائمةٌ مغلقةٌ كانت ستوجب فتحَ شاشةِ
 * إعدادٍ لإضافة كلمة.
 *
 * فهذه بينهما: تُفتح فتُرى الخياراتُ كلُّها، ويُبحث فيها بالكتابة، وما لم
 * يكن فيها يُضاف بزرٍّ يقول ما سيُضيفه — فالإضافةُ قصدٌ لا سهوُ إملاء.
 */
export function Combo({
  id,
  value,
  onChange,
  options,
  placeholder = 'اختر',
  allowNew = true,
  clearLabel = 'بلا تحديد',
  disabled,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  placeholder?: string;
  /** يُسمح بكتابة قيمةٍ ليست في القائمة فتصير خيارًا فيما بعد. */
  allowNew?: boolean;
  clearLabel?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  /* الإغلاقُ بالنقر خارجها وبالهروب — ما يفعله المستخدم غريزيًّا. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setTerm('');
  }, [open]);

  const typed = term.trim();
  const shown = useMemo(
    () =>
      typed
        ? options.filter((o) => o.toLowerCase().includes(typed.toLowerCase()))
        : options,
    [options, typed],
  );
  /* «إضافة» تظهر لما لا مثيل له — لا لما يختلف عنه بحرف كبيرٍ أو صغير. */
  const isNew =
    allowNew &&
    typed.length > 0 &&
    !options.some((o) => o.toLowerCase() === typed.toLowerCase());

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <div className={`combo${open ? ' is-open' : ''}`} ref={boxRef}>
      <button
        id={id}
        type="button"
        className="combo-btn"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`combo-val${value ? '' : ' is-empty'}`}>
          {value || placeholder}
        </span>
        <Icon name="chevronDown" size={14} />
      </button>

      {open && (
        <div className="combo-pop">
          <div className="combo-search">
            <Icon name="search" size={13} />
            <input
              ref={searchRef}
              className="combo-input"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={allowNew ? 'ابحث أو اكتب جديدًا' : 'ابحث'}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                if (isNew) pick(typed);
                else if (shown[0]) pick(shown[0]);
              }}
            />
          </div>

          <ul className="combo-list" role="listbox" aria-labelledby={id}>
            {value && !typed && (
              <li>
                <button
                  type="button"
                  className="combo-opt is-clear"
                  onClick={() => pick('')}
                >
                  <Icon name="x" size={13} />
                  {clearLabel}
                </button>
              </li>
            )}

            {shown.map((o) => (
              <li key={o}>
                <button
                  type="button"
                  role="option"
                  aria-selected={o === value}
                  className={`combo-opt${o === value ? ' is-on' : ''}`}
                  onClick={() => pick(o)}
                >
                  <span className="combo-tick">
                    {o === value && <Icon name="check" size={13} />}
                  </span>
                  {o}
                </button>
              </li>
            ))}

            {shown.length === 0 && !isNew && (
              <li className="combo-empty">لا خيارات بعد.</li>
            )}

            {isNew && (
              <li>
                <button
                  type="button"
                  className="combo-opt is-add"
                  onClick={() => pick(typed)}
                >
                  <Icon name="plus" size={13} />
                  إضافة «{typed}»
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
